import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import Decimal from 'decimal.js/decimal.js';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  Component as ItemComponent,
  Item,
  Organization,
  Tax,
  Unit,
} from '../../shared/sdk/models';
import { TaxService } from '../../taxes/tax.service';

import { Router } from '@angular/router';
import {
  NgbActiveModal,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { FeatureModalService } from '../../feature-modal/feature-modal.service';
import { OrganizationService } from '../../organizations/organization.service';
import { validNumberValidator } from '../../shared/directives/valid-number.directive';
import { FormService } from '../../shared/services/form.service';
import {
  LanguageService,
  locales,
} from '../../shared/services/language.service';
import { SKUValidator } from '../../shared/validators/SKU.validator';
import { GlobalSubscriptionService } from '../../subscriptions/global-subscription.service';
import { UnitService } from '../../units/unit.service';
import { ItemService } from '../item.service';

@Component({
  selector: 'app-item-add',
  templateUrl: './item-add.component.html',
  styleUrls: ['./item-add.component.css'],
})
export class ItemAddComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() item: Item;
  @ViewChild('itemName') itemName: ElementRef;
  activeOrg: Organization;
  activeOrg$: Observable<Organization>;
  addingComponent = false;
  addComponentForm: FormGroup;
  currency: string;
  currencyId$: Observable<string>;
  decimalPlaces = 4;
  hasInventoryAddon$: Observable<boolean>;
  hasUnitsAddon$: Observable<boolean>;
  inputLocale: any = {};
  itemForm: FormGroup;
  isPriceGross = false;
  isPurchasePriceGross = false;
  loading: any = {
    units: true,
  };
  sent: boolean;
  showAdvanced = false;
  taxes: Tax[];
  taxes$: Observable<Tax[]>;
  total: number;
  updating: boolean;
  units: Unit[];
  units$: Observable<Unit[]>;
  selectedLocale: string;
  hasInventory: boolean = false;
  protected onDestroy$: Subject<void> = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private featureModalService: FeatureModalService,
    private formService: FormService,
    private itemService: ItemService,
    private languageService: LanguageService,
    private orgService: OrganizationService,
    private router: Router,
    private globalSubscriptionService: GlobalSubscriptionService,
    private taxService: TaxService,
    private unitService: UnitService,
  ) {}

  ngOnInit(): void {
    this.activeOrg$ = this.orgService.active.pipe(
      tap((org) => {
        this.activeOrg = org;

        this.decimalPlaces =
          this.orgService.findDefault(org, 'decimal_places')?.value || 4;
      }),
      takeUntil(this.onDestroy$),
    );

    // Form control
    this.itemForm = this.fb.group({
      id: [],
      name: ['', Validators.required],
      description: [''],
      unit: [''],
      price: [
        undefined,
        Validators.compose([
          Validators.required,
          validNumberValidator(this.languageService),
        ]),
      ],
      priceGross: [undefined],
      purchasePrice: [undefined],
      purchasePriceGross: [undefined],
      taxIds: this.fb.array([]),
      purchaseTaxIds: this.fb.array([]),
      SKU: [
        ,
        [Validators.pattern(/^\S*$/)],
        // [this.validateUniqueSKU.bind(this)],
        SKUValidator.createValidator(
          this.orgService,
          this.itemService,
          this.item ? this.item.id : null,
        ),
      ],
      EAN: [],
      type: ['outgoing'],
      unitIds: [],
      trackInventory: [false],
      _components: [[]],
      _data: this.fb.array([]),
      custom: [],
    });

    this.addComponentForm = this.fb.group({
      componentSearch: [],
      item: [],
      itemId: [],
      amount: [],
    });

    this.itemForm.controls.taxIds.disable();
    this.itemForm.controls.unitIds.disable();

    this.hasInventoryAddon$ = this.globalSubscriptionService.has(
      'inventory',
    ) as Observable<boolean>;
    this.hasUnitsAddon$ = this.globalSubscriptionService.has(
      'units',
    ) as Observable<boolean>;

    if (!this.item) {
      this.item = new Item();
    } else {
      this.item = new Item(this.item);
      this.isPriceGross = typeof this.item.priceGross !== 'undefined';
      this.isPurchasePriceGross =
        typeof this.item.purchasePriceGross !== 'undefined';
      this.itemForm.patchValue(this.item);

      this.hasInventory = this.item.inventories?.some(
        (inventory) => inventory.amount !== 0,
      );

      if (this.hasInventory) {
        this.itemForm.controls.trackInventory.setValue(true);
        this.itemForm.controls.trackInventory.disable();
        this.showAdvanced = true;
      }

      if (this.isPriceGross) {
        this.itemForm.get('price').clearValidators();
        this.itemForm.get('priceGross').setValidators(Validators.required);
      }

      const components = this.itemForm.controls._components.value;

      if (components.length) {
        this.loading.components = true;
        this.itemForm.controls.trackInventory.disable();

        // Load component's item
        this.orgService.active
          .pipe(
            take(1),
            switchMap((org) =>
              this.itemService.get(org.id, {
                where: { id: { inq: components.map((c: any) => c.itemId) } },
              }),
            ),
          )
          .subscribe(({ body: items }) => {
            items.forEach((component) => {
              const cmpIdx = components.findIndex(
                (cmp: any) => cmp.itemId === component.id,
              );

              components[cmpIdx].item = component;
            });

            this.itemForm.controls._components.patchValue(components);
            this.loading.components = false;
          });

        this.showAdvanced = true;
      }

      if (this.itemForm.controls._data.value.length) {
        this.showAdvanced = true;
      }
    }

    // After patch form to prevent firing if editing and has trackInventory set
    // from before
    combineLatest([
      this.itemForm.controls.trackInventory.valueChanges,
      this.hasInventoryAddon$,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([trackInventory, hasInventoryAddon]) => {
        if (trackInventory && !hasInventoryAddon) {
          // Open feature modal
          this.featureModalService
            .open('inventory', {
              clearSidebar: false,
              appendTo: 'body',
            })
            // Close item add modal if redirected to subscription change
            .then(() => this.activeModal.dismiss())
            .catch(() => {});

          this.itemForm.controls.trackInventory.patchValue(false, {
            onlySelf: true,
            emitEvent: false,
          });
        } else {
          this.addingComponent = false;
          this.resetComponentAddForm();
        }
      });

    merge(
      ...this.formService.getCtrlChangeSub(this.itemForm, [
        'price',
        'priceGross',
        'taxIds',
      ]),
    )
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => this.onChangeInput());

    this.itemForm
      .get('type')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((type) => {
        const ctrl = this.itemForm.get(
          this.isPurchasePriceGross ? 'purchasePriceGross' : 'purchasePrice',
        );
        if (type === '' || type === 'incoming') {
          ctrl.setValidators(
            Validators.compose([
              Validators.required,
              validNumberValidator(this.languageService),
            ]),
          );
        } else {
          ctrl.clearValidators();
        }
      });

    merge(
      ...this.formService.getCtrlChangeSub(this.itemForm, [
        'purchasePrice',
        'purchasePriceGross',
        'purchaseTaxIds',
      ]),
    )
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => this.onChangePurchaseInput());

    this.currencyId$ = this.activeOrg$.pipe(
      map((org) => this.orgService.findDefault(org, 'currencyId').value),
      filter((currencyId) => !!currencyId),
      takeUntil(this.onDestroy$),
    );

    this.taxes$ = this.activeOrg$.pipe(
      switchMap((org) => this.taxService.get(org.id)),
      map(({ body: taxes }) => {
        this.taxes = taxes; // left for onChangeInput to still function, needs rewrite to observable
        this.itemForm.controls.taxIds.enable();
        return taxes;
      }),
      takeUntil(this.onDestroy$),
    );

    this.units$ = this.activeOrg$.pipe(
      switchMap((org) => this.unitService.get(org.id)),
      map(({ body: units }) => {
        this.itemForm.controls.unitIds.enable();
        return units;
      }),
      takeUntil(this.onDestroy$),
    );

    if (this.item.taxIds?.length) {
      this.item.taxIds.forEach((taxId) => {
        this.addTax(this.initTax(taxId));
      });
    } else {
      let taxId: string;

      combineLatest([this.activeOrg$, this.taxes$])
        .pipe(take(1))
        .subscribe(([org, taxes]) => {
          if (org.taxSubject === true && !this.item.id) {
            taxId = taxes.find((tax) => tax.classification === 'standard')
              ? taxes.find((tax) => tax.classification === 'standard').id
              : null;
          }
          this.addTax(this.initTax(taxId));
        });
    }

    if (this.item.purchaseTaxIds?.length) {
      this.item.purchaseTaxIds.forEach((taxId) => {
        this.addPurchaseTax(this.initTax(taxId));
      });
    } else {
      this.addPurchaseTax(this.initTax());
    }
  }

  ngAfterViewInit(): void {
    //   setTimeout(() => this.itemName.nativeElement.focus(), 100);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  componentSearch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      filter((term) => term.length > 0),
      switchMap((term: string) =>
        this.findItems(term).pipe(catchError((_) => of([]))),
      ),
      map((results) => {
        this.loading.componentSearch = false;
        return results;
      }),
      takeUntil(this.onDestroy$),
    );

  onChange_data(dataFA: FormArray): void {
    this.itemForm.controls._data = dataFA;
  }

  onClose(): void {
    this.router.navigate([this.router.url.split(';')[0]]);
    this.activeModal.close();
  }

  onSave(): void {
    this.sent = true;

    const nameCtrl = this.itemForm.controls.name;
    nameCtrl.setValue(nameCtrl.value.trim());

    if (this.itemForm.valid) {
      this.loading.save = true;
      const item: any = this.itemForm.getRawValue();

      const priceField = !this.isPriceGross ? 'price' : 'priceGross';
      const purchasePriceField = !this.isPurchasePriceGross
        ? 'purchasePrice'
        : 'purchasePriceGross';

      delete item[!this.isPriceGross ? 'priceGross' : 'price'];
      delete item[
        !this.isPurchasePriceGross ? 'purchasePriceGross' : 'purchasePrice'
      ];

      item[priceField] = item[priceField] || 0;
      item[priceField] = this.languageService.unformatNumber(item[priceField]);

      item[purchasePriceField] = item[purchasePriceField] || 0;
      item[purchasePriceField] = this.languageService.unformatNumber(
        item[purchasePriceField],
      );

      this.updating = !!item.id;

      this.item = item;

      item._components = item._components.map((cmp: any) => {
        delete cmp.item;
        return cmp;
      });

      this.activeOrg$
        .pipe(
          take(1),
          switchMap((org) => {
            if (!this.updating) {
              return this.itemService.create(org.id, item);
            } else {
              return this.itemService.update(item.id, item);
            }
          }),
        )
        .subscribe(
          (data: Item) => {
            data.taxes = this.taxes;
            this.router.navigate([this.router.url.split(';')[0]]);
            this.activeModal.close(data);
          },
          () => {},
        )
        .add(() => (this.loading.save = false));
    } else {
      if (nameCtrl.hasError('required')) {
        // In case of name error switch to default locale of input
        this.activeOrg$
          .pipe(take(1))
          .subscribe(
            (org) =>
              (this.inputLocale = locales.find(
                (l) => l.symbol === org.locale.substring(0, 2),
              )),
          );
      }

      if (this.itemForm.controls.SKU.hasError('notUnique')) {
        this.showAdvanced = true;
      }
    }
  }

  onCancleAddComponent(): void {
    this.resetComponentAddForm();
    this.addingComponent = false;
  }

  onChangeInputLocale(locale: string): void {
    this.inputLocale = locale;
  }

  onConfirmAddComponent(): void {
    const componentsCtrl = this.itemForm.controls._components;

    componentsCtrl.patchValue([
      ...componentsCtrl.value,
      this.addComponentForm.value,
    ]);

    this.resetComponentAddForm();
    this.addingComponent = false;

    this.itemForm.controls.trackInventory.disable();
  }

  onRemoveComponent(idx: number): void {
    const components = this.itemForm.controls._components.value;

    this.itemForm.controls._components.patchValue([
      ...components.slice(0, idx),
      ...components.slice(idx + 1),
    ]);

    if (!this.itemForm.controls._components.value.length) {
      this.itemForm.controls.trackInventory.enable();
    }
  }

  onSelectComponent(match: NgbTypeaheadSelectItemEvent): void {
    if (match && match.item) {
      const item = match.item as Item;
      const component: Partial<ItemComponent> = {};

      component.itemId = item.id;
      component.item = item;
      component.amount = 1;
      delete component.id;

      this.addComponentForm.patchValue(component);
      this.addComponentForm.controls.componentSearch.patchValue(
        component.item.name,
        {
          emitEvent: false,
          onlySelf: true,
        },
      );
      this.addComponentForm.controls.componentSearch.disable();

      match.preventDefault();
    }
  }

  onToggleAddingComponent(): void {
    this.addingComponent = !this.addingComponent;
    this.resetComponentAddForm();
  }

  onTogglePriceGross(gross: boolean): void {
    if (this.isPriceGross === gross) {
      return;
    }

    const validators = Validators.compose([
      Validators.required,
      validNumberValidator(this.languageService),
    ]);

    this.isPriceGross = gross;

    if (this.isPriceGross) {
      this.itemForm.controls.priceGross.setValidators(validators);
      this.itemForm.controls.price.clearValidators();

      this.itemForm.controls.priceGross.patchValue(
        this.itemForm.controls.price.value,
        { emitEvent: false },
      );
      this.itemForm.controls.price.patchValue(undefined, { emitEvent: false });
    } else {
      this.itemForm.controls.price.setValidators(validators);
      this.itemForm.controls.priceGross.clearValidators();

      this.itemForm.controls.price.patchValue(
        this.itemForm.controls.priceGross.value,
        { emitEvent: false },
      );
      this.itemForm.controls.priceGross.patchValue(undefined, {
        emitEvent: false,
      });
    }

    this.onChangeInput();
  }

  /**
   * Copy of the onTogglePriceGross function modified for the
   * purchasePrice fields
   * @param gross
   */
  onTogglePurchasePriceGross(gross: boolean): void {
    if (this.isPurchasePriceGross === gross) {
      return;
    }

    const validators = Validators.compose([
      Validators.required,
      validNumberValidator(this.languageService),
    ]);

    this.isPurchasePriceGross = gross;

    if (this.isPurchasePriceGross) {
      this.itemForm.controls.purchasePriceGross.setValidators(validators);
      this.itemForm.controls.purchasePrice.clearValidators();

      this.itemForm.controls.purchasePriceGross.patchValue(
        this.itemForm.controls.purchasePrice.value,
        { emitEvent: false },
      );
      this.itemForm.controls.purchasePrice.patchValue(undefined, {
        emitEvent: false,
      });
    } else {
      this.itemForm.controls.purchasePrice.setValidators(validators);
      this.itemForm.controls.purchasePriceGross.clearValidators();

      this.itemForm.controls.purchasePrice.patchValue(
        this.itemForm.controls.purchasePriceGross.value,
        { emitEvent: false },
      );
      this.itemForm.controls.purchasePriceGross.patchValue(undefined, {
        emitEvent: false,
      });
    }

    this.onChangePurchaseInput();
  }

  get taxIdsFA(): FormArray {
    return this.itemForm.controls.taxIds as FormArray;
  }

  private addTax(tax): void {
    this.taxIdsFA.push(tax);
  }

  get purchaseTaxIdsFA(): FormArray {
    return this.itemForm.controls.purchaseTaxIds as FormArray;
  }

  private addPurchaseTax(tax): void {
    this.purchaseTaxIdsFA.push(tax);
  }

  private findItems(term: string): Observable<Item[]> {
    this.loading.componentSearch = true;

    return this.activeOrg$.pipe(
      take(1),
      switchMap((org) => this.itemService.find(org.id, term)),
      map(({ body }) => body),
    );
  }

  private initTax(taxId = ''): FormControl {
    return this.fb.control(taxId);
  }

  private onChangeInput(): void {
    const item = this.itemForm.value;
    item.taxes = this.taxes;
    const priceField = !this.isPriceGross ? 'price' : 'priceGross';

    if (
      item[priceField] &&
      this.languageService.isCalculatable(item[priceField])
    ) {
      let price = item[priceField] || 0;
      item.price = undefined;
      item.priceGross = undefined;

      price = this.languageService.unformatNumber(price);
      price = new Decimal(price).toDP(this.decimalPlaces, Decimal.ROUND_FLOOR);

      item[priceField] = price;

      this.total = this.itemService.calculateTotal(item, this.decimalPlaces);
    }
  }

  private onChangePurchaseInput(): void {
    const item = this.itemForm.value;
    // item.taxes = this.taxes;
    const priceField = !this.isPurchasePriceGross
      ? 'purchasePrice'
      : 'purchasePriceGross';

    if (
      item[priceField] &&
      this.languageService.isCalculatable(item[priceField])
    ) {
      let price = item[priceField] || 0;
      item.purchasePrice = undefined;
      item.purchasePriceGross = undefined;

      price = this.languageService.unformatNumber(price);
      price = new Decimal(price).toDP(this.decimalPlaces, Decimal.ROUND_FLOOR);

      item[priceField] = price;

      // this.total = this.itemService.calculateTotal(item);
    }
  }

  private resetComponentAddForm(): void {
    this.addComponentForm.controls.componentSearch.enable();
    this.addComponentForm.reset();
    this.addComponentForm.controls.amount.setValue(1, { emitEvent: false });
  }
}
